/**
 * @author yuanzeyu
 * @date 2020/10/16
 * @desc
 */
import { client } from '@ont-dev/ontology-dapi';
import axios from 'axios';
import _ from 'lodash';
import { message } from 'antd';
import DApp from './index';
import { CyanoTags, LocaleNames } from '../../enum';
import { WalletVcItem } from '../vc/types';
import { getPreJwtString, getWalletJwtString } from '../vc/vcSdk';
import Urls from '../../enum/urls';

/**
 * Cyano
 */
class Cyano extends DApp {
  // eslint-disable-next-line no-useless-constructor
  constructor(ontId: string) {
    super(ontId);
    localStorage.setItem(LocaleNames.LOGIN_TYPE, 'Cyano');
  }

  static tryGetLocaleDApp(): Cyano | null {
    const ont = localStorage.getItem(LocaleNames.ONT);
    const jwt = localStorage.getItem(LocaleNames.JWT_CACHE_NAME);
    const scoreId = localStorage.getItem(LocaleNames.SCORE_ID_CACHE);
    const score = localStorage.getItem(LocaleNames.SCORE_CACHE); // may be empty
    if (ont) {
      const cyano = new Cyano(ont);
      cyano.setJwtStr(jwt || '')
        .setScoreResultId(scoreId || '')
        .setScore(score || '0')
        .setAddressListWithJwt();
      return cyano;
    }
    return null;
  }

  static registerCyanoClient() {
    client.registerClient({
      logMessages: false,
      logWarnings: false,
    });
  }

  static async getOntIdWithCyano() {
    Cyano.registerCyanoClient();
    return client.api.identity.getIdentity();
  }

  static async getVcWithCyano() {
    Cyano.registerCyanoClient();
    return client.api.identity.getCredentials();
  }

  /**
   * get latest wallet address vc from Cyano.
   */
  public async getAddressJwt() {
    const allVc = await Cyano.getVcWithCyano();
    const walletVcList = allVc.find((item) => item.tags.includes(CyanoTags.WalletAddressList));
    return walletVcList ? walletVcList.credential : '';
  }

  /**
   * add a new address list vc.
   * @param list
   * @private
   */
  private async updateAddress2LocalAndCyano(list: WalletVcItem[]) {
    const publicKeyId = `${this.ontId}#keys-1`; // todo temp use static id.
    const preVc = getPreJwtString(this.ontId, publicKeyId, list);
    const { data } = await client.api.message.signMessage({
      message: preVc,
      useIdentity: true,
    });
    const vc = getWalletJwtString(preVc, client.api.utils.hexToStr(data));
    this.setJwtStr(vc);
    this.setAddressListWithJwt();
    await client.api.identity.addCredential({
      tags: ['wallet-list-vc'],
      credential: vc,
    });
  }

  /**
   * add a wallet address to cyano;
   */
  public addAddress2Cyano(wallet: WalletVcItem) {
    const list = _.uniqBy([wallet, ...this.addressList], 'address');
    return this.updateAddress2LocalAndCyano(list);
  }

  /**
   * remove a wallet address to cyano;
   * @param wallet
   */
  public removeAddress2Cyano(wallet: WalletVcItem) {
    _.remove(this.addressList, (val) => val.address === wallet.address);
    return this.updateAddress2LocalAndCyano(this.addressList);
  }

  /**
   * load or refresh address list from latest Cyano vc;
   */
  public async loadAddressList() {
    const latestVc = await this.getAddressJwt();
    this.setJwtStr(latestVc);
    this.setAddressListWithJwt();
  }

  private async getScoreId() {
    const { data } = await axios({
      method: 'post',
      baseURL: process.env.REACT_APP_API_URL,
      url: Urls.GetCyanoScore,
      data: {
        ontId: this.ontId,
        dataProviderId: 1,
        apiId: 1,
        credentialId: 1,
        requestDataArg: {
          ontId: this.ontId,
          index: 1,
          data_type: 1,
          cred: this.jwtStr,
        },
      },
    });
    this.setScoreResultId(data.data.orderId);
  }

  public async fetchScore() {
    if (this.addressList.length === 0) {
      return;
    }
    try {
      await this.getScoreId();
      const result = await this.fetchScorePms();
      this.setScore(result);
    } catch (e) {
      message.error(e.message);
    }
  }
}

export default Cyano;
